import i18n from '@ohif/i18n';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ThemeProvider } from '@vindr/ui';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { GlobalProvider } from '@vindr/workspace-core/contexts/GlobalContext';
import GlobalLayoutProvider from './contexts/GlobalLayoutContext';

import 'antd/dist/reset.css';
import 'styles/index.css';

/**
 * Google Tag Manager
 */
import TagManager from 'react-gtm-module';

const tagManagerArgs = window?.config?.gtmArgs;

if (tagManagerArgs) {
  TagManager.initialize(tagManagerArgs);
}

if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
  navigator.serviceWorker
    .register('/service-worker.js')
    .then((registration) => {
      console.log('Service Worker registered with scope:', registration.scope);
    })
    .catch((error) => {
      console.error('Service Worker registration failed:', error);
    });
}

const container = document.getElementById('app');
const root = createRoot(container);
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 0,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retryOnMount: false,
      // Invalidate cache after 10s
      staleTime: 10 * 1000, // 10s
      cacheTime: 5 * 60 * 1000, // 5m
      onError: (e) => console.error(e),
    },
    mutations: {
      retry: 0,
    },
  },
});

root.render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter basename={process.env.PUBLIC_URL || '/'}>
        <I18nextProvider i18n={i18n}>
          <ThemeProvider>
            <GlobalProvider>
              <GlobalLayoutProvider>
                <App />
              </GlobalLayoutProvider>
            </GlobalProvider>
          </ThemeProvider>
        </I18nextProvider>
      </BrowserRouter>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  </StrictMode>
);
